import type { ReactNode } from "react"

import { Eyebrow, Icon } from "../../BatteryOnlyProposal/primitives"
import {
  formatCreatedDate,
  formatUsd,
} from "../../BatteryOnlyProposal/view-helpers"
import { STALE_FIGURE_CLASS } from "../../ProposalPresentation/updates/stale-figure"
import type { SolarHeadline, SolarHeroSavings } from "../solar-presentation"

const LEAD_IN_CLASS = `font-display max-w-[580px] text-[length:var(--step-2)] leading-[1.15] font-normal tracking-[-0.025em] text-bp-text-soft lg:text-[32px]`

const savingsLeadIn = ({
  familyName,
  termYears,
  kind,
}: SolarHeroSavings): string =>
  kind === `compute`
    ? `Over the next ${termYears} years, solar + your compute node will save ${
        familyName ? `the ${familyName} family` : `you`
      } roughly`
    : kind === `net`
      ? `Over the next ${termYears} years, going solar will save ${
          familyName ? `the ${familyName} family` : `you`
        } roughly`
      : `Over the next ${termYears} years, solar will cut ${
          familyName ? `the ${familyName} family's` : `your`
        } utility bills by roughly`

/**
 * The status, the savings statement and its chips. While `savingsPending`,
 * the statement keeps its space with a placeholder, so a proposal that has
 * savings never flashes "Not available yet" first. Out-of-date savings keep
 * their figure; the seller page marks it `stale` and its Updates panel owns
 * the recalculation. `statusAction` sits beside the status (the seller page's
 * Contract status).
 */
export function Hero({
  headline,
  savings,
  savingsPending = false,
  statusAction = null,
  stale = false,
}: {
  headline: SolarHeadline
  savings: SolarHeroSavings | null
  savingsPending?: boolean
  statusAction?: ReactNode
  /** Seller page only: underline the savings figure as out of date. */
  stale?: boolean
}): JSX.Element {
  const created = formatCreatedDate(headline.createdAt)
  const {
    utilityName: utility,
    offsetPct,
    offsetIsAurora,
    offsetIncludesAiCompute,
  } = headline

  return (
    <section
      id="hero"
      className="relative scroll-mt-24 overflow-hidden px-6 pt-14 pb-20 lg:px-16 lg:pt-[70px] lg:pb-[90px]"
    >
      <div className="pointer-events-none absolute inset-0 bg-[image:radial-gradient(ellipse_80%_50%_at_100%_0%,var(--bp-accent-soft),transparent_55%)]" />
      <div className="relative mb-10 flex items-center justify-between gap-4 lg:mb-14">
        {headline.statusLabel ? (
          <Eyebrow>
            <span className="text-bp-success">●</span> {headline.statusLabel}
          </Eyebrow>
        ) : null}
        {statusAction}
        {created ? (
          <Eyebrow className="ml-auto text-bp-text-dim">{created}</Eyebrow>
        ) : null}
      </div>

      <div className="relative min-w-0">
        {savings ? (
          <>
            <p className={LEAD_IN_CLASS}>{savingsLeadIn(savings)}</p>
            <div className="font-display mt-4 bg-[image:var(--bp-grad-accent)] bg-clip-text text-[clamp(4rem,12vw,12.25rem)] leading-[0.92] font-semibold tracking-[-0.05em] text-transparent tabular-nums">
              <span
                data-testid={stale ? `hero-savings-stale` : undefined}
                className={stale ? STALE_FIGURE_CLASS : undefined}
              >
                {formatUsd(savings.lifetimeSavings)}
                {stale ? <span className="sr-only"> (out of date)</span> : null}
              </span>
            </div>
          </>
        ) : savingsPending ? (
          <div
            role="status"
            aria-label="Loading your savings"
            className="motion-safe:animate-pulse"
          >
            {/* Sized in the statement's own units: two lead-in lines, then the figure. */}
            <div className="flex max-w-[580px] flex-col gap-[0.15em] py-[0.075em] text-[length:var(--step-2)] lg:text-[32px]">
              <div className="h-[1em] rounded-md bg-bp-surface" />
              <div className="h-[1em] w-3/4 rounded-md bg-bp-surface" />
            </div>
            <div className="mt-4 h-[0.92em] w-[min(100%,2.75em)] rounded-xl bg-bp-surface text-[clamp(4rem,12vw,12.25rem)]" />
          </div>
        ) : (
          <>
            <p className={LEAD_IN_CLASS}>Your 25-year solar savings estimate</p>
            <div className="font-display mt-4 text-[clamp(2.5rem,6vw,4.5rem)] leading-none font-semibold tracking-[-0.04em] text-bp-text-muted">
              Not available yet
            </div>
          </>
        )}
        <div className="mt-6 flex flex-wrap items-center gap-x-9 gap-y-3 text-[length:var(--step--1)] text-bp-text-muted">
          {savings && utility ? (
            <span className="inline-flex items-center gap-2">
              <Icon name="bolt" className="size-3.5 text-bp-accent" />
              vs. staying with {utility}
            </span>
          ) : null}
          {savings?.breakEvenYear != null ? (
            <span className="inline-flex items-center gap-2">
              <Icon name="clock" className="size-3.5 text-bp-success" />
              Break-even in year {savings.breakEvenYear}
            </span>
          ) : null}
          {offsetPct != null ? (
            <span className="inline-flex items-center gap-2">
              <Icon name="home" className="size-3.5 text-bp-cyan" />
              {Math.round(offsetPct)}%{` `}
              {offsetIncludesAiCompute
                ? `of home + compute node usage`
                : `of household usage`}
              {offsetIsAurora ? `, per the Aurora design` : null}
            </span>
          ) : null}
        </div>
      </div>
    </section>
  )
}
